import { Heading, Paragraph } from '@audio-network/componentlibrary'
import { RelatedSparkle } from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import TrackList from '@/components/shared/TrackList/TrackList'
import TrackListSkeleton from '@/components/shared/TrackList/TrackList.skeleton'
import { FIGARO, MUSIC_TEAM } from '@/constants/trackSources'

import {
	useHandpickedTracks,
	useShowAISimilarTracks,
} from './TrackRelatedTracks.hooks'
import styles from './TrackRelatedTracks.module.css'
import { TrackRelatedTracksProps } from './TrackRelatedTracks.types'

const TrackRelatedTracks = ({
	handpickedTracks: handpickedTracksFromProps,
	onHandpickedFetchBegin,
	track,
	className,
	...props
}: TrackRelatedTracksProps): JSX.Element => {
	const { t } = useTranslation(['track'])
	const containerRef = useRef<HTMLDivElement>(null)

	const setContainerRef = (element: HTMLDivElement | null): void => {
		containerRef.current = element
	}
	const { handpickedTracks } = useHandpickedTracks(
		track.id,
		handpickedTracksFromProps,
		onHandpickedFetchBegin
	)
	const similarTracksFromAI = useShowAISimilarTracks(
		containerRef,
		track.id,
		handpickedTracks
	)

	const handpickedReady = handpickedTracks !== null
	const aiReady = similarTracksFromAI !== null
	const hasHandPickedTracks = handpickedReady && handpickedTracks.length > 0
	const hasAISimilarTracks = aiReady && similarTracksFromAI.length > 0
	const showEmptyState =
		handpickedReady && aiReady && !hasHandPickedTracks && !hasAISimilarTracks

	const HandPickedTracks = (): JSX.Element | null => {
		if (handpickedTracks === null) {
			return <TrackListSkeleton />
		}

		if (!hasHandPickedTracks) {
			return null
		}

		return (
			<div>
				<Heading level={2} size="S" className={styles.sectionHeading}>
					{t('similar-tracks-staff-picked')}
				</Heading>
				<TrackList
					skipQueueInitialisation
					source={MUSIC_TEAM}
					tracks={handpickedTracks}
				/>
			</div>
		)
	}

	const AISimilarTracks = (): JSX.Element | null => {
		if (similarTracksFromAI === null) {
			return <TrackListSkeleton />
		}

		if (!hasAISimilarTracks) {
			return null
		}

		return (
			<div>
				<Heading level={2} size="S" className={styles.aiHeading}>
					<RelatedSparkle />
					{t('similar-tracks-ai')}
				</Heading>
				<TrackList
					skipQueueInitialisation
					download={{
						canDownloadAll: true,
						name: 'Similar tracks',
					}}
					source={FIGARO}
					tracks={similarTracksFromAI}
				/>
			</div>
		)
	}

	const NoRelatedTracks = (): JSX.Element => {
		return (
			<>
				<Heading level={2} size="S">
					{t('no-similar-tracks-header')}
				</Heading>
				<Paragraph>
					{t('no-similar-tracks-description', {
						trackTitle: `'${track.title}'`,
					})}
				</Paragraph>
			</>
		)
	}

	return (
		<div
			ref={setContainerRef}
			className={clsx(styles.relatedTracks, className)}
			{...props}
		>
			<HandPickedTracks />
			<AISimilarTracks />
			{showEmptyState ? <NoRelatedTracks /> : null}
		</div>
	)
}

export default TrackRelatedTracks
